import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  DesktopWindows as DesktopIcon,
  Build as BuildIcon,
  MonetizationOn as MoneyIcon,
  PieChart as PieChartIcon,
  FilterList as FilterIcon,
  AccountBalance as DrawerIcon,
  Phone as PhoneIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import {
  MaintenanceStats,
  InventoryStats,
  SalesStats,
  getMaintenanceStats,
  getInventoryStats,
  getSalesStats,
  getSimpleDrawerStats
} from '../services/reportService';
import { getAllDebtStats, DebtStatsByType, onDebtUpdate } from '../services/debtService';
import InstapayVodafoneWalletReport from '../components/reports/InstapayVodafoneWalletReport';
// import { formatCurrency } from '../utils/constants'; // استبدل بـ formatCurrency
import { MaintenanceRecord, MaintenanceStatus, UserRole } from '../utils/types';
import { useCurrency } from '../hooks/useCurrency';
import { getMaintenanceByDeliveryDateRange, getMaintenanceByDateRangeAndBranch } from '../services/maintenanceService';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// ألوان للرسوم البيانية
// const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B'];

// نوع الفترة الزمنية للتقارير
type TimeRange = 'day' | 'yesterday' | 'week' | 'month' | 'year' | 'custom' | 'specific-date';

const ReportsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { formatCurrency } = useCurrency();

  // إضافة CSS للأنيميشن
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.7; }
        100% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // دالة للتحقق من صلاحيات الوصول للتواريخ
  const canAccessTimeRange = (timeRange: TimeRange): boolean => {
    if (!currentUser) return false;

    // المدير العام يمكنه الوصول لكل شيء
    if (currentUser.role === UserRole.ADMIN) return true;

    // مدير الفرع يمكنه الوصول لكل شيء عدا السنة الكاملة
    if (currentUser.role === UserRole.BRANCH_MANAGER) {
      return timeRange !== 'year';
    }

    // باقي الأدوار يمكنهم الوصول فقط لليوم وأمس والأسبوع
    return ['day', 'yesterday', 'week'].includes(timeRange);
  };

  // دالة للحصول على رسالة عدم الصلاحية
  const getPermissionMessage = (timeRange: TimeRange): string => {
    if (currentUser?.role === UserRole.BRANCH_MANAGER && timeRange === 'year') {
      return 'تحتاج صلاحية المدير العام لعرض تقارير السنة الكاملة';
    }
    return 'ليس لديك صلاحية لعرض تقارير هذه الفترة الزمنية';
  };

  // حالة التبويبات
  const [currentTab, setCurrentTab] = useState<string>('overview');

  // حالة نطاق التاريخ
  const [timeRange, setTimeRange] = useState<TimeRange>('day'); // تغيير الافتراضي إلى اليوم
  const [startDate, setStartDate] = useState<Date | null>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [specificDate, setSpecificDate] = useState<Date | null>(new Date());

  // حالة التقارير
  const [loading, setLoading] = useState<boolean>(true);
  const [salesData, setSalesData] = useState<SalesStats | null>(null);
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceStats | null>(null);
  const [inventoryData, setInventoryData] = useState<InventoryStats | null>(null);
  const [drawerSimpleStats, setDrawerSimpleStats] = useState<any>(null);
  const [electronicWalletsData, setElectronicWalletsData] = useState<any>(null);
  const [storeData, setStoreData] = useState<any>(null);
  const [debtStats, setDebtStats] = useState<{
    sales: DebtStatsByType;
    maintenance: DebtStatsByType;
    total: DebtStatsByType;
  } | null>(null);

  // حالة عرض تفاصيل الطلبات
  const [showOrdersModal, setShowOrdersModal] = useState<boolean>(false);
  const [detailedOrders, setDetailedOrders] = useState<MaintenanceRecord[]>([]);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [ordersModalTitle, setOrdersModalTitle] = useState<string>('');
  const [ordersTotalAmount, setOrdersTotalAmount] = useState<number>(0);
  const [ordersTotalPaid, setOrdersTotalPaid] = useState<number>(0);
  const [ordersTotalRemaining, setOrdersTotalRemaining] = useState<number>(0);

  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // حالة التحديث التلقائي
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // دالة تحديث البيانات
  const refreshData = async (showNotification = true) => {
    setIsRefreshing(true);
    setLastRefresh(new Date());

    try {
      await loadReportsData();

      if (showNotification) {
        setSnackbar({
          open: true,
          message: '🔄 تم تحديث البيانات بنجاح',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      if (showNotification) {
        setSnackbar({
          open: true,
          message: '❌ حدث خطأ في تحديث البيانات',
          severity: 'error'
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // إعداد التحديث التلقائي
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refreshData(false); // تحديث صامت بدون إشعار
      }, 30000); // تحديث كل 30 ثانية

      setRefreshInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [autoRefresh, timeRange, startDate, endDate]);

  // تحميل البيانات عند تحميل الصفحة أو تغيير نطاق التاريخ
  useEffect(() => {
    loadReportsData();
  }, [timeRange, startDate, endDate]);

  // التأكد من تعيين القيمة الافتراضية عند تحميل الصفحة
  useEffect(() => {
    console.log('ReportsPage loaded with timeRange:', timeRange);
    // إذا كانت القيمة غير صحيحة أو غير مسموحة، اضبطها على اليوم
    if (!timeRange || !canAccessTimeRange(timeRange)) {
      console.log('Setting timeRange to day');
      setTimeRange('day');
    }
  }, [currentUser, timeRange, canAccessTimeRange]); // يتم تشغيله عند تحميل بيانات المستخدم

  // دالة لجلب الطلبات المفصلة للفترة المحددة
  const loadDetailedOrders = async () => {
    try {
      setLoadingOrders(true);

      // تحديد تواريخ البداية والنهاية بناءً على النطاق المحدد
      let start = startDate || new Date();
      let end = endDate || new Date();

      const now = new Date();

      switch (timeRange) {
        case 'day':
          start = new Date(now);
          start.setHours(0, 0, 0, 0);
          end = new Date(now);
          end.setHours(23, 59, 59, 999);
          setOrdersModalTitle(`طلبات الصيانة لليوم - ${start.toLocaleDateString('ar-EG')}`);
          break;
        case 'yesterday':
          start = new Date(now);
          start.setDate(now.getDate() - 1);
          start.setHours(0, 0, 0, 0);
          end = new Date(now);
          end.setDate(now.getDate() - 1);
          end.setHours(23, 59, 59, 999);
          console.log(`📅 الأمس: من ${start.toISOString()} إلى ${end.toISOString()}`);
          setOrdersModalTitle(`طلبات الصيانة لأمس - ${start.toLocaleDateString('ar-EG')}`);
          break;
        case 'week':
          start = new Date(now);
          start.setDate(now.getDate() - 7);
          end = new Date(now);
          setOrdersModalTitle(`طلبات الصيانة للأسبوع الماضي`);
          break;
        case 'month':
          start = new Date(now);
          start.setDate(now.getDate() - 30);
          end = new Date(now);
          setOrdersModalTitle(`طلبات الصيانة للشهر الماضي`);
          break;
        case 'year':
          start = new Date(now);
          start.setFullYear(now.getFullYear() - 1);
          end = new Date(now);
          setOrdersModalTitle(`طلبات الصيانة للسنة الماضية`);
          break;
        case 'custom':
          setOrdersModalTitle(`طلبات الصيانة من ${start.toLocaleDateString('ar-EG')} إلى ${end.toLocaleDateString('ar-EG')}`);
          break;
        case 'specific-date':
          if (specificDate) {
            start = new Date(specificDate);
            start.setHours(0, 0, 0, 0);
            end = new Date(specificDate);
            end.setHours(23, 59, 59, 999);
            setOrdersModalTitle(`طلبات الصيانة ليوم ${start.toLocaleDateString('ar-EG')}`);
          }
          break;
      }

      // جلب الطلبات للفترة المحددة - تحسين لجلب جميع الطلبات
      console.log(`🔍 جاري جلب الطلبات للفترة: ${start.toISOString()} إلى ${end.toISOString()}`);
      console.log(`📅 النطاق الزمني: ${timeRange}`);

      let orders: MaintenanceRecord[] = [];

      if (timeRange === 'day' || timeRange === 'yesterday' || timeRange === 'specific-date') {
        // للفترات القصيرة، نجرب عدة طرق للبحث

        // 1️⃣ نجرب receivedAt أولاً (تاريخ الاستقبال)
        console.log(`1️⃣ البحث حسب تاريخ الاستقبال (receivedAt)...`);
        const ordersByReceived = await getMaintenanceByDateRangeAndBranch(start, end, currentUser?.branchId);
        console.log(`   📊 تم جلب ${ordersByReceived.length} طلب`);

        // 2️⃣ نجرب deliveredAt (تاريخ التسليم)
        console.log(`2️⃣ البحث حسب تاريخ التسليم (deliveredAt)...`);
        const ordersByDelivered = await getMaintenanceByDeliveryDateRange(start, end, currentUser?.branchId);
        console.log(`   📊 تم جلب ${ordersByDelivered.length} طلب`);

        // 3️⃣ دمج النتائج وإزالة المكررات
        const allOrdersMap = new Map<string, MaintenanceRecord>();

        ordersByReceived.forEach(order => {
          if (order.id) allOrdersMap.set(order.id, order);
        });

        ordersByDelivered.forEach(order => {
          if (order.id && !allOrdersMap.has(order.id)) {
            allOrdersMap.set(order.id, order);
          }
        });

        orders = Array.from(allOrdersMap.values());
        console.log(`✅ إجمالي الطلبات بعد الدمج (بدون تكرار): ${orders.length}`);

      } else {
        // للفترات الطويلة، نستخدم الطريقة الأصلية
        console.log(`📊 البحث للفترة الطويلة...`);
        orders = await getMaintenanceByDeliveryDateRange(start, end);
        console.log(`📊 تم جلب ${orders.length} طلب للفترة الطويلة`);
      }

      console.log(`✅ إجمالي الطلبات المسترجعة النهائية: ${orders.length}`);

      // عرض تفاصيل أول 3 طلبات للتأكد
      if (orders.length > 0) {
        console.log(`📋 عينة من الطلبات:`);
        orders.slice(0, 3).forEach((order, index) => {
          const receivedDate = order.receivedAt instanceof Date ? order.receivedAt.toISOString() : order.receivedAt;
          const deliveredDate = order.deliveredAt instanceof Date ? order.deliveredAt.toISOString() : order.deliveredAt;
          console.log(`   ${index + 1}. رقم الطلب: ${order.orderNumber}, العميل: ${order.customerName}, receivedAt: ${receivedDate}, deliveredAt: ${deliveredDate}`);
        });
      }

      setDetailedOrders(orders);

      // حساب الإجماليات المالية
      let totalAmount = 0;
      let totalPaid = 0;
      let totalRemaining = 0;

      orders.forEach(order => {
        const orderCost = order.finalCost || order.estimatedCost || order.initialCost || 0;
        const orderPaid = order.totalPaid || order.paid || 0;
        const orderRemaining = order.remainingAmount || Math.max(0, orderCost - orderPaid);

        totalAmount += orderCost;
        totalPaid += orderPaid;
        totalRemaining += orderRemaining;
      });

      setOrdersTotalAmount(totalAmount);
      setOrdersTotalPaid(totalPaid);
      setOrdersTotalRemaining(totalRemaining);

      setShowOrdersModal(true);

      // تحديث وقت آخر تحديث
      setLastRefresh(new Date());

      console.log(`📋 تم جلب ${orders.length} طلب صيانة للفترة المحددة`);
      console.log(`📅 الفترة: ${start.toLocaleDateString('ar-EG')} - ${end.toLocaleDateString('ar-EG')}`);
      console.log(`🔍 نوع البحث: ${timeRange}`);
      console.log(`💰 إجمالي التكلفة: ${formatCurrency(totalAmount)}`);
      console.log(`💚 إجمالي المدفوع: ${formatCurrency(totalPaid)}`);
      console.log(`💸 إجمالي المتبقي: ${formatCurrency(totalRemaining)}`);
      
      // عرض تفاصيل الطلبات في الكونسول للمساعدة في التشخيص
      if (orders.length > 0) {
        console.log('📋 تفاصيل الطلبات:');
        orders.forEach((order, index) => {
          console.log(`${index + 1}. طلب #${order.orderNumber} - ${order.customerName} - مدفوع: ${order.totalPaid || order.paid || 0}`);
        });
      } else {
        console.log('⚠️ لم يتم العثور على أي طلبات للفترة المحددة');
        console.log('💡 تأكد من وجود طلبات صيانة لها مدفوعات في هذه الفترة');
      }
    } catch (error) {
      console.error('❌ خطأ في جلب الطلبات المفصلة:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء جلب الطلبات. يرجى المحاولة مرة أخرى.',
        severity: 'error'
      });
    } finally {
      setLoadingOrders(false);
    }
  };

  // فحص الطلبات التي تحتاج إصلاح عند تحميل الصفحة
  useEffect(() => {
    // تم إزالة فحص الطلبات القديمة مؤقتاً
    // يمكن إضافته لاحقاً عند الحاجة
  }, [currentUser]);

  // الاستماع لتحديثات الديون
  useEffect(() => {
    const unsubscribe = onDebtUpdate(() => {
      // إعادة تحميل إحصائيات الديون عند حدوث تحديث
      if (startDate && endDate) {
        getAllDebtStats(startDate, endDate)
          .then(debtsData => {
            setDebtStats(debtsData);
            console.log('🔄 تم تحديث إحصائيات الديون تلقائياً');
          })
          .catch(error => {
            console.error('Error updating debt stats:', error);
          });
      }
    });

    return unsubscribe;
  }, [startDate, endDate]);

  // تحميل بيانات المتجر الإلكتروني
  const loadStoreData = async (start: Date, end: Date) => {
    try {
      console.log('🛒 جلب بيانات المتجر الإلكتروني...');

      const storePaymentsQuery = query(
        collection(db, 'drawerTransactions'),
        where('type', '==', 'store_payment')
      );

      const storePaymentsSnapshot = await getDocs(storePaymentsQuery);

      let totalAmount = 0;
      let totalCount = 0;
      let cashAmount = 0;
      let cashCount = 0;
      let walletAmount = 0;
      let walletCount = 0;

      storePaymentsSnapshot.forEach(doc => {
        const payment = doc.data();
        const paymentDate = payment.paymentDate?.toDate() || payment.createdAt?.toDate();

        if (paymentDate && paymentDate >= start && paymentDate <= end) {
          const amount = payment.amount || 0;
          totalAmount += amount;
          totalCount += 1;

          if (payment.paymentMethod === 'cash') {
            cashAmount += amount;
            cashCount += 1;
          } else if (['vodafone_wallet', 'instapay', 'etisalat_wallet'].includes(payment.paymentMethod)) {
            walletAmount += amount;
            walletCount += 1;
          }
        }
      });

      console.log(`✅ تم جلب ${totalCount} دفعة متجر بإجمالي ${totalAmount} جنيه`);

      return {
        totalAmount,
        totalCount,
        cashAmount,
        cashCount,
        walletAmount,
        walletCount
      };
    } catch (error) {
      console.error('خطأ في تحميل بيانات المتجر:', error);
      return {
        totalAmount: 0,
        totalCount: 0,
        cashAmount: 0,
        cashCount: 0,
        walletAmount: 0,
        walletCount: 0
      };
    }
  };

  // تحميل بيانات المحافظ الإلكترونية
  const loadElectronicWalletsData = async (start: Date, end: Date) => {
    try {
      // 1️⃣ جلب دفعات الصيانة
      const maintenanceQuery = query(
        collection(db, 'maintenance'),
        where('status', '==', 'delivered')
      );

      const maintenanceSnapshot = await getDocs(maintenanceQuery);

      const walletsData = {
        vodafone: { amount: 0, count: 0 },
        instapay: { amount: 0, count: 0 },
        etisalat: { amount: 0, count: 0 },
        totalAmount: 0,
        totalCount: 0
      };

      // معالجة دفعات الصيانة
      maintenanceSnapshot.forEach(doc => {
        const record = doc.data();

        // فلترة التاريخ
        if (record.deliveredAt) {
          const deliveredDate = record.deliveredAt && typeof record.deliveredAt === 'object' && 'toDate' in record.deliveredAt
            ? (record.deliveredAt as any).toDate()
            : new Date(record.deliveredAt);

          if (deliveredDate < start || deliveredDate > end) {
            return;
          }
        } else {
          return;
        }

        const paymentMethod = record.paymentMethod;
        const paidAmount = record.totalPaid || record.paid || 0;

        if (paymentMethod === 'vodafone_wallet') {
          walletsData.vodafone.amount += paidAmount;
          walletsData.vodafone.count += 1;
          walletsData.totalAmount += paidAmount;
          walletsData.totalCount += 1;
        } else if (paymentMethod === 'instapay') {
          walletsData.instapay.amount += paidAmount;
          walletsData.instapay.count += 1;
          walletsData.totalAmount += paidAmount;
          walletsData.totalCount += 1;
        } else if (paymentMethod === 'etisalat_wallet') {
          walletsData.etisalat.amount += paidAmount;
          walletsData.etisalat.count += 1;
          walletsData.totalAmount += paidAmount;
          walletsData.totalCount += 1;
        }
      });

      // 2️⃣ جلب دفعات الديون من drawerTransactions
      console.log('🔍 جلب دفعات الديون بالمحافظ الإلكترونية...');
      const debtPaymentsQuery = query(
        collection(db, 'drawerTransactions'),
        where('type', '==', 'debt_payment')
      );

      const debtPaymentsSnapshot = await getDocs(debtPaymentsQuery);
      let debtWalletCount = 0;

      debtPaymentsSnapshot.forEach(doc => {
        const payment = doc.data();

        // فلترة التاريخ
        if (payment.paymentDate) {
          const paymentDate = payment.paymentDate && typeof payment.paymentDate === 'object' && 'toDate' in payment.paymentDate
            ? (payment.paymentDate as any).toDate()
            : new Date(payment.paymentDate);

          if (paymentDate < start || paymentDate > end) {
            return;
          }
        } else {
          return;
        }

        const paymentMethod = payment.paymentMethod;
        const amount = payment.amount || 0;

        // إضافة دفعات الديون بالمحافظ الإلكترونية
        if (paymentMethod === 'vodafone_wallet') {
          walletsData.vodafone.amount += amount;
          walletsData.vodafone.count += 1;
          walletsData.totalAmount += amount;
          walletsData.totalCount += 1;
          debtWalletCount++;
        } else if (paymentMethod === 'instapay') {
          walletsData.instapay.amount += amount;
          walletsData.instapay.count += 1;
          walletsData.totalAmount += amount;
          walletsData.totalCount += 1;
          debtWalletCount++;
        } else if (paymentMethod === 'etisalat_wallet') {
          walletsData.etisalat.amount += amount;
          walletsData.etisalat.count += 1;
          walletsData.totalAmount += amount;
          walletsData.totalCount += 1;
          debtWalletCount++;
        }
      });

      console.log(`✅ تم جلب ${debtWalletCount} دفعة دين بالمحافظ الإلكترونية`);
      console.log('📊 إجمالي المحافظ الإلكترونية:', walletsData);

      return walletsData;
    } catch (error) {
      console.error('خطأ في تحميل بيانات المحافظ الإلكترونية:', error);
      return {
        vodafone: { amount: 0, count: 0 },
        instapay: { amount: 0, count: 0 },
        etisalat: { amount: 0, count: 0 },
        totalAmount: 0,
        totalCount: 0
      };
    }
  };

  // تحميل بيانات التقارير
  const loadReportsData = async () => {
    try {
      setLoading(true);
      console.log('Loading reports data for timeRange:', timeRange);

      // التحقق من الصلاحيات
      if (!canAccessTimeRange(timeRange)) {
        console.warn('Access denied for time range:', timeRange);
        setLoading(false);
        return;
      }

      // تحديد تواريخ البداية والنهاية بناءً على النطاق المحدد
      let start = startDate || new Date();
      let end = endDate || new Date();

      const now = new Date();

      switch (timeRange) {
        case 'day':
          start = new Date(now);
          start.setHours(0, 0, 0, 0);
          end = new Date(now);
          end.setHours(23, 59, 59, 999);
          break;
        case 'yesterday':
          start = new Date(now);
          start.setDate(now.getDate() - 1);
          start.setHours(0, 0, 0, 0);
          end = new Date(now);
          end.setDate(now.getDate() - 1);
          end.setHours(23, 59, 59, 999);
          console.log(`📅 تحميل بيانات الأمس: من ${start.toISOString()} إلى ${end.toISOString()}`);
          break;
        case 'week':
          start = new Date(now);
          start.setDate(now.getDate() - 7);
          end = new Date(now);
          break;
        case 'month':
          start = new Date(now);
          start.setDate(now.getDate() - 30);
          end = new Date(now);
          break;
        case 'year':
          start = new Date(now);
          start.setFullYear(now.getFullYear() - 1);
          end = new Date(now);
          break;
        case 'custom':
          // استخدام التواريخ المحددة بواسطة المستخدم
          break;
        case 'specific-date':
          // استخدام تاريخ محدد واحد
          if (specificDate) {
            start = new Date(specificDate);
            start.setHours(0, 0, 0, 0);
            end = new Date(specificDate);
            end.setHours(23, 59, 59, 999);
          }
          break;
      }

      // استخدام خدمة التقارير للحصول على البيانات
      // 🔧 للطلبات المُسلمة، نستخدم getMaintenanceByDeliveryDateRange بدلاً من getMaintenanceStats
      // للحصول على إحصائيات دقيقة بناءً على تاريخ التسليم وليس الاستقبال
      let maintenanceStats: MaintenanceStats;
      
      if (timeRange === 'day' || timeRange === 'yesterday' || timeRange === 'specific-date') {
        // لليوم الواحد، نجلب جميع الطلبات - بحث مزدوج (receivedAt + deliveredAt)
        console.log(`🔍 جلب طلبات الصيانة للفترة: ${start.toISOString()} إلى ${end.toISOString()}`);

        // 1️⃣ البحث حسب receivedAt
        const recordsByReceived = await getMaintenanceByDateRangeAndBranch(start, end, currentUser?.branchId);
        console.log(`   📊 تم جلب ${recordsByReceived.length} طلب حسب receivedAt`);

        // 2️⃣ البحث حسب deliveredAt
        const recordsByDelivered = await getMaintenanceByDeliveryDateRange(start, end, currentUser?.branchId);
        console.log(`   📊 تم جلب ${recordsByDelivered.length} طلب حسب deliveredAt`);

        // 3️⃣ دمج النتائج وإزالة المكررات
        const allRecordsMap = new Map<string, any>();

        recordsByReceived.forEach(record => {
          if (record.id) allRecordsMap.set(record.id, record);
        });

        recordsByDelivered.forEach(record => {
          if (record.id && !allRecordsMap.has(record.id)) {
            allRecordsMap.set(record.id, record);
          }
        });

        const allRecords = Array.from(allRecordsMap.values());
        console.log(`✅ إجمالي الطلبات بعد الدمج: ${allRecords.length}`);

        // حساب الإحصائيات من جميع الطلبات التي لها مدفوعات
        const statusCounts: Record<string, number> = {};
        let totalIncome = 0;
        let totalPartsCost = 0;
        let totalPartsRevenue = 0;
        
        allRecords.forEach((record: any) => {
          // إحصائيات الحالة
          statusCounts[record.status] = (statusCounts[record.status] || 0) + 1;
          
          // إجمالي الدخل - استخدام المبلغ المدفوع الفعلي
          const paidAmount = record.totalPaid || record.paid || 0;
          if (paidAmount > 0) {
            totalIncome += paidAmount;
          }
          
          // حساب تكلفة قطع الغيار وإيراداتها
          if (record.usedParts && record.usedParts.length > 0) {
            record.usedParts.forEach((part: any) => {
              const quantity = Number(part.quantity) || 0;
              const purchasePrice = Number(part.purchasePrice) || 0;
              const sellingPrice = Number(part.sellingPrice) || Number(part.price) || purchasePrice;
              
              totalPartsCost += purchasePrice * quantity; // التكلفة الفعلية
              totalPartsRevenue += sellingPrice * quantity; // الإيرادات
            });
          }
        });
        
        const totalPartsProfit = totalPartsRevenue - totalPartsCost;
        const totalMaintenanceFees = totalIncome - totalPartsRevenue;
        const netProfit = totalIncome - totalPartsCost;
        const totalCount = allRecords.length;

        maintenanceStats = {
          total: totalCount,
          totalIncome,
          totalRevenue: totalIncome,
          statusCounts,
          dailyCounts: { [start.toISOString().split('T')[0]]: totalCount },
          completedCount: statusCounts['completed'] || 0,
          pendingCount: statusCounts['pending'] || 0,
          inProgressCount: statusCounts['in-progress'] || 0,
          deliveredCount: statusCounts['delivered'] || 0,
          cancelledCount: statusCounts['cancelled'] || 0,
          totalPartsCost,
          totalPartsRevenue,
          totalPartsProfit,
          totalMaintenanceFees,
          netProfit,
          partsUsageStats: {
            totalPartsUsed: allRecords.reduce((sum: number, record: any) => 
              sum + (record.usedParts?.length || 0), 0),
            uniquePartsCount: new Set(allRecords.flatMap((record: any) => 
              record.usedParts?.map((part: any) => part.name) || [])).size,
            mostUsedParts: []
          }
        };
        
        console.log(`🔧 تم جلب ${totalCount} طلب صيانة في ${start.toISOString().split('T')[0]}`);
        console.log(`🔧 الصيانة: ${totalCount} طلب، إجمالي مدفوع: ${totalIncome}`);
        console.log(`📊 هذه الإحصائيات بناءً على المدفوعات الفعلية`);
      } else {
        // للفترات الطويلة، نستخدم الدالة العادية
        maintenanceStats = await getMaintenanceStats(start, end);
      }
      const inventoryStats = await getInventoryStats();
      const salesStats = await getSalesStats(start, end);
      
      const simpleDrawerStats = await getSimpleDrawerStats(start, end);
      setDrawerSimpleStats(simpleDrawerStats);

      // تحميل بيانات المحافظ الإلكترونية
      const walletsData = await loadElectronicWalletsData(start, end);
      setElectronicWalletsData(walletsData);

      // تحميل بيانات المتجر الإلكتروني
      const storeStats = await loadStoreData(start, end);
      setStoreData(storeStats);

      // تحميل إحصائيات الديون
      console.log(`📊 Loading debt stats from ${start.toISOString()} to ${end.toISOString()}`);
      const debtsData = await getAllDebtStats(start, end);
      console.log('📊 Debt stats loaded:', debtsData);
      setDebtStats(debtsData);

      // تعيين البيانات في الحالة
      setSalesData(salesStats);
      setMaintenanceData(maintenanceStats);
      setInventoryData(inventoryStats);

    } catch (error) {
      console.error('Error loading reports data:', error);
    } finally {
      setLoading(false);
    }
  };

  // تغيير التبويب الحالي
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  // تغيير نطاق التاريخ
  const handleTimeRangeChange = (event: any) => {
    const newTimeRange = event.target.value;

    // التحقق من الصلاحيات قبل التغيير
    if (!canAccessTimeRange(newTimeRange)) {
      alert(getPermissionMessage(newTimeRange));
      return;
    }

    setTimeRange(newTimeRange);
  };

  // عرض شاشة التحميل
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress />
      </Box>
    );
  }

  // عرض رسالة عدم الصلاحية إذا لم يكن المستخدم مخولاً لعرض الفترة المحددة
  if (!canAccessTimeRange(timeRange)) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          التقارير والإحصائيات
        </Typography>

        <Alert severity="error" sx={{ mt: 3 }}>
          <AlertTitle>ليس لديك صلاحية</AlertTitle>
          <Typography variant="body1">
            {getPermissionMessage(timeRange)}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setTimeRange('day')}
              size="small"
            >
              العودة لتقارير اليوم
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // بيانات للرسم البياني الدائري لحالات الصيانة
  const maintenanceStatusData = maintenanceData?.statusCounts ?
    Object.keys(maintenanceData.statusCounts).map(status => ({
      name: status,
      value: maintenanceData.statusCounts[status]
    })) : [];

  // بيانات للرسم البياني الدائري للمنتجات الأكثر مبيعًا
  const topSellingProducts = salesData?.topSellingItems || [];

  // بيانات للرسم البياني الشريطي للمبيعات حسب اليوم
  const dailySalesData = salesData?.dailySales ?
    Object.keys(salesData.dailySales).map(date => ({
      date,
      amount: salesData.dailySales[date]
    })) : [];

  // بيانات للرسم البياني الشريطي للصيانة حسب اليوم
  const dailyMaintenanceData = maintenanceData?.dailyCounts ?
    Object.keys(maintenanceData.dailyCounts).map(date => ({
      date,
      count: maintenanceData.dailyCounts[date]
    })) : [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        التقارير والإحصائيات
      </Typography>

      {/* رسالة الصلاحيات */}
      {currentUser && currentUser.role !== UserRole.ADMIN && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>معلومات الصلاحيات</AlertTitle>
          {currentUser.role === UserRole.BRANCH_MANAGER ? (
            <Typography variant="body2">
              كمدير فرع، يمكنك الوصول لجميع التقارير عدا تقارير السنة الكاملة (تحتاج صلاحية المدير العام).
            </Typography>
          ) : (
            <Typography variant="body2">
              يمكنك الوصول لتقارير اليوم وأمس والأسبوع فقط. للوصول لفترات أطول، تواصل مع مدير الفرع أو المدير العام.
            </Typography>
          )}
        </Alert>
      )}

      {/* أدوات التصفية */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" color="primary">
            الفترة المحددة: {
              timeRange === 'day' ? 'اليوم' :
              timeRange === 'yesterday' ? 'أمس' :
              timeRange === 'week' ? 'آخر 7 أيام' :
              timeRange === 'month' ? 'آخر 30 يوم' :
              timeRange === 'year' ? 'السنة' :
              timeRange === 'specific-date' ? 'يوم محدد' :
              timeRange === 'custom' ? 'مخصص' : 'غير محدد'
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            (القيمة: {timeRange})
          </Typography>

          {/* أزرار التحكم في التحديث */}
          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            <Button
              variant={autoRefresh ? "contained" : "outlined"}
              color={autoRefresh ? "success" : "primary"}
              size="small"
              onClick={() => setAutoRefresh(!autoRefresh)}
              startIcon={autoRefresh ? <span>🔄</span> : <span>⏸️</span>}
            >
              {autoRefresh ? 'تحديث تلقائي' : 'تحديث يدوي'}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => refreshData(true)}
              disabled={isRefreshing}
              startIcon={isRefreshing ? <span>⏳</span> : <span>🔄</span>}
            >
              {isRefreshing ? 'جاري التحديث...' : 'تحديث الآن'}
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isRefreshing && (
                <span style={{ fontSize: '12px' }}>🔄</span>
              )}
              <Typography variant="caption" color="text.secondary">
                آخر تحديث: {lastRefresh.toLocaleTimeString('ar-EG')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>الفترة الزمنية</InputLabel>
              <Select
                value={timeRange}
                label="الفترة الزمنية"
                onChange={handleTimeRangeChange}
                onOpen={() => console.log('Select opened with value:', timeRange)}
                sx={{
                  '& .MuiSelect-select': {
                    backgroundColor: timeRange === 'day' ? 'primary.light' : 'inherit',
                    color: timeRange === 'day' ? 'primary.contrastText' : 'inherit'
                  }
                }}
              >
                <MenuItem value="day">اليوم</MenuItem>
                <MenuItem value="yesterday">أمس</MenuItem>
                <MenuItem value="week" disabled={!canAccessTimeRange('week')}>
                  آخر 7 أيام
                  {!canAccessTimeRange('week') && ' (غير مسموح)'}
                </MenuItem>
                <MenuItem value="month" disabled={!canAccessTimeRange('month')}>
                  آخر 30 يوم
                  {!canAccessTimeRange('month') && ' (غير مسموح)'}
                </MenuItem>
                <MenuItem value="year" disabled={!canAccessTimeRange('year')}>
                  السنة
                  {!canAccessTimeRange('year') && ' (صلاحية المدير فقط)'}
                </MenuItem>
                <MenuItem value="specific-date" disabled={!canAccessTimeRange('specific-date')}>
                  يوم محدد
                  {!canAccessTimeRange('specific-date') && ' (غير مسموح)'}
                </MenuItem>
                <MenuItem value="custom" disabled={!canAccessTimeRange('custom')}>
                  مخصص
                  {!canAccessTimeRange('custom') && ' (غير مسموح)'}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {timeRange === 'specific-date' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="التاريخ المحدد"
                type="date"
                size="small"
                fullWidth
                value={specificDate ? specificDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setSpecificDate(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {timeRange === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="من تاريخ"
                  type="date"
                  size="small"
                  fullWidth
                  value={startDate ? startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="إلى تاريخ"
                  type="date"
                  size="small"
                  fullWidth
                  value={endDate ? endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item>
            <Button
              variant="contained"
              onClick={loadReportsData}
              startIcon={<FilterIcon />}
            >
              تطبيق
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* تبويبات التقارير */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="overview" label="نظرة عامة" icon={<PieChartIcon />} iconPosition="start" />
          <Tab value="sales" label="المبيعات" icon={<MoneyIcon />} iconPosition="start" />
          <Tab value="maintenance" label="الصيانة" icon={<BuildIcon />} iconPosition="start" />
          <Tab value="inventory" label="المخزون" icon={<DesktopIcon />} iconPosition="start" />
          <Tab value="cashbox" label="الخزينة" icon={<DrawerIcon />} iconPosition="start" />
          <Tab value="electronic-wallets" label="محافظ انستاباي فودافون" icon={<PhoneIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* محتوى التبويب: نظرة عامة */}
      {currentTab === 'overview' && (
        <Box>
          {/* بطاقات الإحصائيات */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    💰
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>🛒</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      إجمالي المبيعات
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(salesData?.totalSales || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        {salesData?.totalTransactions || 0} معاملة
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      💳 شامل
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)',
                  }
                }}
                onClick={loadDetailedOrders}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      borderRadius: '8px',
                      width: 48,
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0.5
                    }}>
                      <ViewIcon sx={{ fontSize: 32 }} />
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        📋 عدد طلبات الصيانة
                      </Typography>
                      <Typography variant="h3" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {maintenanceData?.total || 0}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        للفترة المحددة
                      </Typography>
                    </Box>

                    <Box sx={{
                      mt: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      animation: 'pulse 2s infinite'
                    }}>
                      <LaunchIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2" fontWeight="600">
                        اضغط لعرض التفاصيل
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    🔧
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>⚙️</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      إيرادات الصيانة
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(maintenanceData?.totalIncome || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        {maintenanceData?.total || 0} طلب
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      🔧 خدمات
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #FF5722 0%, #D84315 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(255, 87, 34, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(255, 87, 34, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    💸
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>🔩</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      تكلفة قطع الغيار
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(maintenanceData?.totalPartsCost || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        سعر الشراء
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      📦 مخزون
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(156, 39, 176, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    💵
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>🏷️</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      إيرادات قطع الغيار
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(maintenanceData?.totalPartsRevenue || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        سعر البيع
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      👥 عملاء
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 188, 212, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(0, 188, 212, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    💎
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>💰</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      ربح قطع الغيار
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(maintenanceData?.totalPartsProfit || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        هامش الربح
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      📈 {maintenanceData?.totalPartsRevenue && maintenanceData?.totalPartsCost ?
                        Math.round(((maintenanceData.totalPartsProfit || 0) / (maintenanceData.totalPartsRevenue || 1)) * 100) : 0}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: (maintenanceData?.netProfit || 0) >= 0 ?
                  'linear-gradient(135deg, #8BC34A 0%, #689F38 100%)' :
                  'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: (maintenanceData?.netProfit || 0) >= 0 ?
                  '0 8px 32px rgba(139, 195, 74, 0.3)' :
                  '0 8px 32px rgba(244, 67, 54, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: (maintenanceData?.netProfit || 0) >= 0 ?
                    '0 12px 40px rgba(139, 195, 74, 0.4)' :
                    '0 12px 40px rgba(244, 67, 54, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    {(maintenanceData?.netProfit || 0) >= 0 ? '📊' : '📉'}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>
                        {(maintenanceData?.netProfit || 0) >= 0 ? '✅' : '❌'}
                      </span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      صافي ربح الصيانة
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(maintenanceData?.netProfit || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        بعد خصم التكاليف
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {(maintenanceData?.netProfit || 0) >= 0 ? '📈 ربح' : '📉 خسارة'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: (inventoryData?.lowStockCount || 0) > 0 ?
                  'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)' :
                  'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: (inventoryData?.lowStockCount || 0) > 0 ?
                  '0 8px 32px rgba(255, 152, 0, 0.3)' :
                  '0 8px 32px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: (inventoryData?.lowStockCount || 0) > 0 ?
                    '0 12px 40px rgba(255, 152, 0, 0.4)' :
                    '0 12px 40px rgba(76, 175, 80, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    📦
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>
                        {(inventoryData?.lowStockCount || 0) > 0 ? '⚠️' : '✅'}
                      </span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      المخزون منخفض
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {inventoryData?.lowStockCount || 0}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        عنصر يحتاج تجديد
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {(inventoryData?.lowStockCount || 0) > 0 ? '🚨 تنبيه' : '👍 جيد'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #673AB7 0%, #512DA8 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(103, 58, 183, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(103, 58, 183, 0.4)',
                }
              }}
              onClick={() => setCurrentTab('electronic-wallets')}
              >
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    📱
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>💳</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      المحافظ الإلكترونية
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(electronicWalletsData?.totalAmount || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        {electronicWalletsData?.totalCount || 0} معاملة
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      📲 رقمي
                    </Typography>
                  </Box>

                  {/* تفاصيل المحافظ */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={`📱 ${formatCurrency(electronicWalletsData?.vodafone?.amount || 0)}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(230, 0, 18, 0.8)',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                    <Chip
                      label={`💳 ${formatCurrency(electronicWalletsData?.instapay?.amount || 0)}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(25, 118, 210, 0.8)',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                    <Chip
                      label={`📲 ${formatCurrency(electronicWalletsData?.etisalat?.amount || 0)}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 152, 0, 0.8)',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>

                  {/* مؤشر قابلية النقر */}
                  <Box sx={{
                    mt: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 1,
                    px: 1,
                    py: 0.2
                  }}>
                    <LaunchIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" fontWeight="500">
                      اضغط لعرض التفاصيل
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة المتجر الإلكتروني */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 188, 212, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(0, 188, 212, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    🛒
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>🛍️</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      المتجر الإلكتروني
                    </Typography>
                  </Box>

                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {formatCurrency(storeData?.totalAmount || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        {storeData?.totalCount || 0} طلب
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      🌐 أونلاين
                    </Typography>
                  </Box>

                  {/* تفاصيل المتجر */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={`💵 ${formatCurrency(storeData?.cashAmount || 0)}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(76, 175, 80, 0.8)',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                    <Chip
                      label={`💳 ${formatCurrency(storeData?.walletAmount || 0)}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(103, 58, 183, 0.8)',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة إجمالي الإيرادات */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(255, 215, 0, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(255, 215, 0, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    🏆
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.8,
                      mr: 1.5
                    }}>
                      <span style={{ fontSize: '18px' }}>💰</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      إجمالي الإيرادات
                    </Typography>
                  </Box>

                  <Typography variant="h4" component="div" fontWeight="bold" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1
                  }}>
                    {formatCurrency(
                      (salesData?.totalSales || 0) +
                      (maintenanceData?.totalIncome || 0) +
                      (electronicWalletsData?.totalAmount || 0) +
                      (storeData?.totalAmount || 0)
                    )}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.3
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        جميع المصادر
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      🎯 إجمالي
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة صافي الربح الإجمالي */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: (
                  (salesData?.totalSales || 0) +
                  (maintenanceData?.netProfit || 0) +
                  (electronicWalletsData?.totalAmount || 0) +
                  (storeData?.totalAmount || 0) -
                  (drawerSimpleStats?.totalExpenses || 0)
                ) >= 0 ?
                  'linear-gradient(135deg, #00E676 0%, #00C853 100%)' :
                  'linear-gradient(135deg, #FF1744 0%, #D50000 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: (
                  (salesData?.totalSales || 0) +
                  (maintenanceData?.netProfit || 0) +
                  (electronicWalletsData?.totalAmount || 0) +
                  (storeData?.totalAmount || 0) -
                  (drawerSimpleStats?.totalExpenses || 0)
                ) >= 0 ?
                  '0 8px 32px rgba(0, 230, 118, 0.3)' :
                  '0 8px 32px rgba(255, 23, 68, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: (
                    (salesData?.totalSales || 0) +
                    (maintenanceData?.netProfit || 0) +
                    (electronicWalletsData?.totalAmount || 0) +
                    (storeData?.totalAmount || 0) -
                    (drawerSimpleStats?.totalExpenses || 0)
                  ) >= 0 ?
                    '0 12px 40px rgba(0, 230, 118, 0.4)' :
                    '0 12px 40px rgba(255, 23, 68, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    {(salesData?.totalSales || 0) +
                      (maintenanceData?.netProfit || 0) +
                      (electronicWalletsData?.totalAmount || 0) +
                      (storeData?.totalAmount || 0) -
                      (drawerSimpleStats?.totalExpenses || 0) >= 0 ? '📈' : '📉'}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.5,
                      mr: 1
                    }}>
                      <span style={{ fontSize: '24px' }}>
                        {(salesData?.totalSales || 0) +
                          (maintenanceData?.netProfit || 0) +
                          (electronicWalletsData?.totalAmount || 0) +
                          (storeData?.totalAmount || 0) -
                          (drawerSimpleStats?.totalExpenses || 0) >= 0 ? '💎' : '⚠️'}
                      </span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      صافي الربح الإجمالي
                    </Typography>
                  </Box>

                  <Typography variant="h4" component="div" fontWeight="bold" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1
                  }}>
                    {formatCurrency(
                      (salesData?.totalSales || 0) +
                      (maintenanceData?.netProfit || 0) +
                      (electronicWalletsData?.totalAmount || 0) +
                      (storeData?.totalAmount || 0) -
                      (drawerSimpleStats?.totalExpenses || 0)
                    )}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        النتيجة النهائية
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {(salesData?.totalSales || 0) +
                        (maintenanceData?.netProfit || 0) +
                        (electronicWalletsData?.totalAmount || 0) +
                        (storeData?.totalAmount || 0) -
                        (drawerSimpleStats?.totalExpenses || 0) >= 0 ? '📈 ربح' : '📉 خسارة'}
                    </Typography>
                  </Box>

                  {/* تفاصيل الربح */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>➕ إيرادات</Typography>
                      <Typography variant="caption" fontWeight="500">
                        {formatCurrency(
                          (salesData?.totalSales || 0) +
                          (maintenanceData?.totalIncome || 0) +
                          (electronicWalletsData?.totalAmount || 0) +
                          (storeData?.totalAmount || 0)
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>➖ مصاريف</Typography>
                      <Typography variant="caption" fontWeight="500">
                        {formatCurrency(drawerSimpleStats?.totalExpenses || 0)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* بطاقات الديون */}
          <Grid container spacing={3} mb={4}>
            {/* بطاقة ديون المبيعات */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 107, 53, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-15deg)'
                  }}>
                    🧾
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      ديون المبيعات
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatCurrency(debtStats?.sales?.totalRemaining || 0)}
                    </Typography>

                    {/* تفاصيل إضافية */}
                    <Box sx={{ mt: 1.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>💰 إجمالي</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.sales?.totalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>✅ مدفوع</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.sales?.totalPaid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>📊 عدد الديون</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {(debtStats?.sales?.activeCount || 0) + (debtStats?.sales?.partiallyPaidCount || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1.5,
                      opacity: 0.8,
                      fontWeight: 'bold'
                    }}>
                      🛒 مبيعات
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة ديون الصيانة */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-15deg)'
                  }}>
                    🔧
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      ديون الصيانة
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatCurrency(debtStats?.maintenance?.totalRemaining || 0)}
                    </Typography>

                    {/* تفاصيل إضافية */}
                    <Box sx={{ mt: 1.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>💰 إجمالي</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.maintenance?.totalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>✅ مدفوع</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.maintenance?.totalPaid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>📊 عدد الديون</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {(debtStats?.maintenance?.activeCount || 0) + (debtStats?.maintenance?.partiallyPaidCount || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1.5,
                      opacity: 0.8,
                      fontWeight: 'bold'
                    }}>
                      🔧 صيانة
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة إجمالي الديون */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: (debtStats?.total?.totalRemaining || 0) > 0 ?
                  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' :
                  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: (debtStats?.total?.totalRemaining || 0) > 0 ?
                  '0 8px 32px rgba(240, 147, 251, 0.3)' :
                  '0 8px 32px rgba(79, 172, 254, 0.3)',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (debtStats?.total?.totalRemaining || 0) > 0 ?
                    '0 12px 40px rgba(240, 147, 251, 0.4)' :
                    '0 12px 40px rgba(79, 172, 254, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-15deg)'
                  }}>
                    📋
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      إجمالي الديون
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatCurrency(debtStats?.total?.totalRemaining || 0)}
                    </Typography>

                    {/* تفاصيل إضافية */}
                    <Box sx={{ mt: 1.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>💰 إجمالي</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.total?.totalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>✅ مدفوع</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.total?.totalPaid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>📊 عدد الديون</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {(debtStats?.total?.activeCount || 0) + (debtStats?.total?.partiallyPaidCount || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1.5,
                      opacity: 0.8,
                      fontWeight: 'bold'
                    }}>
                      {(debtStats?.total?.totalRemaining || 0) > 0 ? '⚠️ متبقي' : '✅ مسدد'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* بيانات المبيعات اليومية */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  المبيعات اليومية
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>التاريخ</TableCell>
                        <TableCell align="right">المبلغ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailySalesData.map((entry) => (
                        <TableRow key={entry.date}>
                          <TableCell>{entry.date}</TableCell>
                          <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  حالات طلبات الصيانة
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>الحالة</TableCell>
                        <TableCell align="right">العدد</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {maintenanceStatusData.map((entry) => (
                        <TableRow key={entry.name}>
                          <TableCell>{entry.name}</TableCell>
                          <TableCell align="right">{entry.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* محتوى التبويب: المبيعات */}
      {currentTab === 'sales' && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  تفاصيل المبيعات اليومية
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>التاريخ</TableCell>
                        <TableCell align="right">المبلغ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailySalesData.map((entry) => (
                        <TableRow key={entry.date}>
                          <TableCell>{entry.date}</TableCell>
                          <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  المنتجات الأكثر مبيعًا
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>المنتج</TableCell>
                        <TableCell align="right">الكمية</TableCell>
                        <TableCell align="right">الإجمالي</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topSellingProducts.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell align="right">{product.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(product.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ملخص المبيعات
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي المبيعات:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {formatCurrency(salesData?.totalSales || 0)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    عدد المعاملات:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {salesData?.totalTransactions || 0}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    متوسط قيمة المعاملة:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {formatCurrency(salesData?.averageTransactionValue || 0)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    إجمالي المبيعات:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {formatCurrency(salesData?.totalSales || 0)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* محتوى التبويب: الصيانة */}
      {currentTab === 'maintenance' && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  تفاصيل طلبات الصيانة
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>التاريخ</TableCell>
                        <TableCell align="right">العدد</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyMaintenanceData.map((entry) => (
                        <TableRow key={entry.date}>
                          <TableCell>{entry.date}</TableCell>
                          <TableCell align="right">{entry.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  توزيع حالات الصيانة
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>الحالة</TableCell>
                        <TableCell align="right">العدد</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {maintenanceStatusData.map((entry) => (
                        <TableRow key={entry.name}>
                          <TableCell>{entry.name}</TableCell>
                          <TableCell align="right">{entry.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ملخص الصيانة
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي الطلبات:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {maintenanceData?.total || 0}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    الطلبات المكتملة:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {maintenanceData?.completedCount || 0}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    الطلبات قيد المعالجة:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {maintenanceData?.inProgressCount || 0}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    إجمالي الإيرادات:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {formatCurrency(maintenanceData?.totalIncome || 0)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    متوسط إيراد الطلب:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {formatCurrency(
                      maintenanceData?.totalIncome && maintenanceData?.total && maintenanceData.total > 0
                        ? maintenanceData.totalIncome / maintenanceData.total
                        : 0
                    )}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    إجمالي قطع الغيار المستخدمة:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {maintenanceData?.partsUsageStats?.totalPartsUsed || 0}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    أنواع قطع الغيار:
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {maintenanceData?.partsUsageStats?.uniquePartsCount || 0}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            {/* قسم جديد مخصص لتكلفة قطع الغيار */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary.main">
                  📊 تحليل تكلفة قطع الغيار بالتفصيل
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, color: 'error.contrastText' }}>
                      <Typography variant="body2" gutterBottom>
                        تكلفة الشراء الإجمالية
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {formatCurrency(maintenanceData?.totalPartsCost || 0)}
                      </Typography>
                      <Typography variant="caption">
                        المبلغ المدفوع للموردين
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText' }}>
                      <Typography variant="body2" gutterBottom>
                        قيمة البيع للعملاء
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {formatCurrency(maintenanceData?.totalPartsRevenue || 0)}
                      </Typography>
                      <Typography variant="caption">
                        المبلغ المحصل من العملاء
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'success.contrastText' }}>
                      <Typography variant="body2" gutterBottom>
                        صافي ربح قطع الغيار
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {formatCurrency(maintenanceData?.totalPartsProfit || 0)}
                      </Typography>
                      <Typography variant="caption">
                        الفرق بين البيع والشراء
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, color: 'info.contrastText' }}>
                      <Typography variant="body2" gutterBottom>
                        هامش الربح
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {maintenanceData?.totalPartsRevenue && maintenanceData?.totalPartsRevenue > 0
                          ? `${(((maintenanceData?.totalPartsProfit || 0) / maintenanceData.totalPartsRevenue) * 100).toFixed(1)}%`
                          : '0%'
                        }
                      </Typography>
                      <Typography variant="caption">
                        نسبة الربح من البيع
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    💡 ملاحظات مهمة:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>تكلفة الشراء:</strong> المبلغ الذي دفعته للموردين لشراء قطع الغيار<br/>
                    • <strong>قيمة البيع:</strong> المبلغ الذي حصلت عليه من العملاء مقابل قطع الغيار<br/>
                    • <strong>صافي الربح:</strong> الفرق بين ما حصلت عليه وما دفعته<br/>
                    • <strong>هامش الربح:</strong> نسبة الربح من قيمة البيع، وهو مؤشر مهم لقياس كفاءة التسعير
                  </Typography>
                </Box>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🔧 تفاصيل إضافية:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>رسوم الصيانة (بدون قطع الغيار):</strong>
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(maintenanceData?.totalMaintenanceFees || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>عدد القطع المستخدمة:</strong>
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {maintenanceData?.partsUsageStats?.totalPartsUsed || 0} قطعة
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* جدول قطع الغيار الأكثر استخداماً */}
          <Grid container spacing={3} mt={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  قطع الغيار الأكثر استخداماً
                </Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>اسم القطعة</TableCell>
                        <TableCell align="center">الكمية المستخدمة</TableCell>
                        <TableCell align="right">تكلفة الشراء</TableCell>
                        <TableCell align="right">إيرادات البيع</TableCell>
                        <TableCell align="right">الربح</TableCell>
                        <TableCell align="right">هامش الربح</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {maintenanceData?.partsUsageStats?.mostUsedParts?.map((part, index) => (
                        <TableRow key={index}>
                          <TableCell>{part.name}</TableCell>
                          <TableCell align="center">{part.quantity}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {formatCurrency(part.cost)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'info.main' }}>
                            {formatCurrency(part.revenue)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: part.profit >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatCurrency(part.profit)}
                          </TableCell>
                          <TableCell align="right">
                            {part.revenue > 0
                              ? `${((part.profit / part.revenue) * 100).toFixed(1)}%`
                              : '0%'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!maintenanceData?.partsUsageStats?.mostUsedParts ||
                        maintenanceData.partsUsageStats.mostUsedParts.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              لا توجد قطع غيار مستخدمة في هذه الفترة
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* محتوى التبويب: المخزون */}
      {currentTab === 'inventory' && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  حالة المخزون حسب الفئة
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>الفئة</TableCell>
                        <TableCell align="right">القيمة</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryData?.categoryDistribution && Object.entries(inventoryData.categoryDistribution).map(([category, value]) => (
                        <TableRow key={category}>
                          <TableCell>{category}</TableCell>
                          <TableCell align="right">{formatCurrency(value as number)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  قيمة المخزون
                </Typography>
                <Box p={3} textAlign="center">
                  <Typography variant="h4" gutterBottom>
                    {formatCurrency(inventoryData?.totalValue || 0)}
                  </Typography>
                  <Typography color="text.secondary">
                    القيمة الإجمالية للمخزون
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  العناصر منخفضة المخزون
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>اسم العنصر</TableCell>
                        <TableCell>الفئة</TableCell>
                        <TableCell align="center">الكمية المتاحة</TableCell>
                        <TableCell align="center">الحد الأدنى</TableCell>
                        <TableCell align="right">سعر الشراء</TableCell>
                        <TableCell align="right">سعر البيع</TableCell>
                        <TableCell align="right">إجمالي القيمة</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryData?.lowStockItems?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="center">{item.minQuantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.purchasePrice)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.sellingPrice)}</TableCell>
                          <TableCell align="right">{formatCurrency((item.purchasePrice || 0) * item.quantity)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* محتوى التبويب: الخزينة */}
      {currentTab === 'cashbox' && (
        <Box>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{
                background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 25%, #81C784 50%, #A5D6A7 75%, #4CAF50 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(76, 175, 80, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.3,
                  zIndex: 0
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(76, 175, 80, 0.35)',
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  zIndex: 1,
                  '&:last-child': { pb: 2.5 }
                }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-10deg)'
                  }}>
                    🛒
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '12px',
                      p: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <span style={{ fontSize: '20px' }}>💰</span>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="inherit" sx={{ fontSize: '0.9rem' }}>
                      إجمالي المبيعات
                    </Typography>
                  </Box>

                  <Typography variant="h3" component="div" fontWeight="700" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatCurrency(drawerSimpleStats?.totalSales || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        للفترة المحددة
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      📈 مبيعات
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{
                background: 'linear-gradient(135deg, #2196F3 0%, #42A5F5 25%, #64B5F6 50%, #90CAF9 75%, #2196F3 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(33, 150, 243, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M40 40l20-20v40l-20-20zm0 0l-20-20v40l20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.4,
                  zIndex: 0
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(33, 150, 243, 0.35)',
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  zIndex: 1,
                  '&:last-child': { pb: 2.5 }
                }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-10deg)',
                    zIndex: 0
                  }}>
                    🔧
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '12px',
                      p: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <span style={{ fontSize: '20px' }}>⚙️</span>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="inherit" sx={{ fontSize: '0.9rem' }}>
                      دخل الصيانة
                    </Typography>
                  </Box>

                  <Typography variant="h3" component="div" fontWeight="700" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatCurrency(drawerSimpleStats?.totalMaintenance || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                        خدمات إصلاح
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                      🔧 صيانة
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{
                background: 'linear-gradient(135deg, #00BCD4 0%, #26C6DA 25%, #4DD0E1 50%, #80DEEA 75%, #00BCD4 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 188, 212, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M50 50m-25 0a25 25 0 1 1 50 0a25 25 0 1 1 -50 0M25 25l50 50M75 25l-50 50M50 15v70M15 50h70'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.3,
                  zIndex: 0
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0, 188, 212, 0.35)',
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  zIndex: 1,
                  '&:last-child': { pb: 2.5 }
                }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-10deg)',
                    zIndex: 0
                  }}>
                    💳
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '12px',
                      p: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <span style={{ fontSize: '20px' }}>💰</span>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="inherit" sx={{ fontSize: '0.9rem' }}>
                      مدفوعات الديون
                    </Typography>
                  </Box>

                  <Typography variant="h3" component="div" fontWeight="700" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatCurrency(drawerSimpleStats?.totalDebtPayments || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                        مضافة للخزينة
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                      💳 تسديد
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{
                background: 'linear-gradient(135deg, #FF5722 0%, #FF7043 25%, #FF8A65 50%, #FFAB91 75%, #FF5722 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(255, 87, 34, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Cpath d='M60 60l30-30v60l-30-30zm0 0l-30-30v60l30-30zM30 30l60 60M90 30l-60 60'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.5,
                  zIndex: 0
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 87, 34, 0.4)',
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  zIndex: 1,
                  '&:last-child': { pb: 2.5 }
                }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-10deg)',
                    zIndex: 0
                  }}>
                    💸
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '12px',
                      p: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <span style={{ fontSize: '20px' }}>📊</span>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="inherit" sx={{ fontSize: '0.9rem' }}>
                      إجمالي المصاريف
                    </Typography>
                  </Box>

                  <Typography variant="h3" component="div" fontWeight="700" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatCurrency(drawerSimpleStats?.totalExpenses || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.3
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        مصروفات عامة
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      💸 خروج
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{
                background: 'linear-gradient(135deg, #E91E63 0%, #EC407A 25%, #F06292 50%, #F48FB1 75%, #E91E63 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(233, 30, 99, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='90' height='90' viewBox='0 0 90 90' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M45 45m-20 0a20 20 0 1 1 40 0a20 20 0 1 1 -40 0M25 25l40 40M65 25l-40 40M45 15v70M15 45h70'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.4,
                  zIndex: 0
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(233, 30, 99, 0.35)',
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  zIndex: 1,
                  '&:last-child': { pb: 2.5 }
                }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-10deg)',
                    zIndex: 0
                  }}>
                    🔄
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '12px',
                      p: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <span style={{ fontSize: '20px' }}>↩️</span>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="inherit" sx={{ fontSize: '0.9rem' }}>
                      إجمالي المرتجعات
                    </Typography>
                  </Box>

                  <Typography variant="h3" component="div" fontWeight="700" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatCurrency(drawerSimpleStats?.totalReturnsFromDrawer || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                        مرتجعات نقدية
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                      🔄 خروج
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{
                background: (drawerSimpleStats?.netTotal || 0) >= 0 ?
                  'linear-gradient(135deg, #8BC34A 0%, #9CCC65 25%, #AED581 50%, #C5E1A5 75%, #8BC34A 100%)' :
                  'linear-gradient(135deg, #F44336 0%, #EF5350 25%, #E57373 50%, #EF9A9A 75%, #F44336 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: (drawerSimpleStats?.netTotal || 0) >= 0 ?
                  '0 4px 20px rgba(139, 195, 74, 0.25)' :
                  '0 4px 20px rgba(244, 67, 54, 0.25)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: (drawerSimpleStats?.netTotal || 0) >= 0 ?
                    `url("data:image/svg+xml,%3Csvg width='110' height='110' viewBox='0 0 110 110' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.07'%3E%3Cpath d='M55 55m-25 0a25 25 0 1 1 50 0a25 25 0 1 1 -50 0M30 30l50 50M80 30l-50 50M55 20v70M20 55h70'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` :
                    `url("data:image/svg+xml,%3Csvg width='110' height='110' viewBox='0 0 110 110' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.07'%3E%3Cpath d='M55 55l-25-25v50l25-25zm0 0l25-25v50l-25-25z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.5,
                  zIndex: 0
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (drawerSimpleStats?.netTotal || 0) >= 0 ?
                    '0 8px 30px rgba(139, 195, 74, 0.35)' :
                    '0 8px 30px rgba(244, 67, 54, 0.35)',
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: 2.5,
                  zIndex: 1,
                  '&:last-child': { pb: 2.5 }
                }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-10deg)',
                    zIndex: 0
                  }}>
                    {(drawerSimpleStats?.netTotal || 0) >= 0 ? '🏦' : '⚠️'}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      borderRadius: '12px',
                      p: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      minHeight: '40px'
                    }}>
                      <span style={{ fontSize: '20px' }}>
                        {(drawerSimpleStats?.netTotal || 0) >= 0 ? '💎' : '📉'}
                      </span>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="600" color="inherit" sx={{ fontSize: '0.9rem' }}>
                      صافي الخزينة
                    </Typography>
                  </Box>

                  <Typography variant="h3" component="div" fontWeight="700" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {formatCurrency(drawerSimpleStats?.netTotal || 0)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.2
                    }}>
                      <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                        شامل الديون
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                      {(drawerSimpleStats?.netTotal || 0) >= 0 ? '📈 إيجابي' : '📉 سلبي'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة إجمالي الإيرادات */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(255, 215, 0, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(255, 215, 0, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px'
                  }}>
                    🏆
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      p: 0.8,
                      mr: 1.5
                    }}>
                      <span style={{ fontSize: '18px' }}>💰</span>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="inherit">
                      إجمالي الإيرادات
                    </Typography>
                  </Box>

                  <Typography variant="h4" component="div" fontWeight="bold" sx={{
                    mb: 0.5,
                    fontSize: '1.2rem',
                    lineHeight: 1.1
                  }}>
                    {formatCurrency(
                      (drawerSimpleStats?.totalSales || 0) +
                      (drawerSimpleStats?.totalMaintenance || 0) +
                      (drawerSimpleStats?.totalDebtPayments || 0)
                    )}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.3
                    }}>
                      <Typography variant="caption" fontWeight="500">
                        جميع المصادر
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      🎯 إجمالي
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* بطاقات الديون في الخزينة */}
          <Grid container spacing={3} mb={4}>
            {/* بطاقة ديون المبيعات */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 107, 53, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-15deg)'
                  }}>
                    🧾
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      ديون المبيعات
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatCurrency(debtStats?.sales?.totalRemaining || 0)}
                    </Typography>

                    {/* تفاصيل إضافية */}
                    <Box sx={{ mt: 1.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>💰 إجمالي</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.sales?.totalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>✅ مدفوع</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.sales?.totalPaid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>📊 عدد الديون</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {(debtStats?.sales?.activeCount || 0) + (debtStats?.sales?.partiallyPaidCount || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1.5,
                      opacity: 0.8,
                      fontWeight: 'bold'
                    }}>
                      🛒 مبيعات
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة ديون الصيانة */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-15deg)'
                  }}>
                    🔧
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      ديون الصيانة
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatCurrency(debtStats?.maintenance?.totalRemaining || 0)}
                    </Typography>

                    {/* تفاصيل إضافية */}
                    <Box sx={{ mt: 1.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>💰 إجمالي</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.maintenance?.totalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>✅ مدفوع</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.maintenance?.totalPaid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>📊 عدد الديون</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {(debtStats?.maintenance?.activeCount || 0) + (debtStats?.maintenance?.partiallyPaidCount || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1.5,
                      opacity: 0.8,
                      fontWeight: 'bold'
                    }}>
                      🔧 صيانة
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* بطاقة إجمالي الديون */}
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{
                background: (debtStats?.total?.totalRemaining || 0) > 0 ?
                  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' :
                  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 2,
                boxShadow: (debtStats?.total?.totalRemaining || 0) > 0 ?
                  '0 8px 32px rgba(240, 147, 251, 0.3)' :
                  '0 8px 32px rgba(79, 172, 254, 0.3)',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (debtStats?.total?.totalRemaining || 0) > 0 ?
                    '0 12px 40px rgba(240, 147, 251, 0.4)' :
                    '0 12px 40px rgba(79, 172, 254, 0.4)',
                }
              }}>
                <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                  {/* أيقونة خلفية */}
                  <Box sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    opacity: 0.08,
                    fontSize: '60px',
                    transform: 'rotate(-15deg)'
                  }}>
                    📋
                  </Box>

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      إجمالي الديون
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatCurrency(debtStats?.total?.totalRemaining || 0)}
                    </Typography>

                    {/* تفاصيل إضافية */}
                    <Box sx={{ mt: 1.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>💰 إجمالي</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.total?.totalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>✅ مدفوع</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {formatCurrency(debtStats?.total?.totalPaid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>📊 عدد الديون</Typography>
                        <Typography variant="caption" fontWeight="500">
                          {(debtStats?.total?.activeCount || 0) + (debtStats?.total?.partiallyPaidCount || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1.5,
                      opacity: 0.8,
                      fontWeight: 'bold'
                    }}>
                      {(debtStats?.total?.totalRemaining || 0) > 0 ? '⚠️ متبقي' : '✅ مسدد'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* تنبيه توضيحي محسن */}
          <Box sx={{ mb: 3 }}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'info.main',
                bgcolor: 'info.light',
                '& .MuiAlert-icon': {
                  fontSize: '28px'
                }
              }}
            >
              <AlertTitle sx={{
                fontWeight: 'bold',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                💰 ملاحظة هامة حول مدفوعات الديون
              </AlertTitle>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  🔄 <strong>التحديث التلقائي:</strong> عند تسديد العملاء للديون نقدياً، يتم إضافة المبلغ المدفوع تلقائياً إلى الخزينة في يوم التسديد.
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  📊 <strong>دقة التقارير:</strong> هذا يضمن انعكاس جميع المدفوعات النقدية بدقة في تقارير الخزينة.
                </Typography>
                <Typography variant="body2">
                  ⏰ <strong>التوقيت:</strong> البيانات تتحدث تلقائياً كل 30 ثانية لضمان الدقة.
                </Typography>
              </Box>
            </Alert>
          </Box>
          
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              تفاصيل الخزينة اليومية
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>التاريخ</TableCell>
                    <TableCell align="right">المبيعات</TableCell>
                    <TableCell align="right">الصيانة</TableCell>
                    <TableCell align="right">مدفوعات الديون</TableCell>
                    <TableCell align="right">المرتجعات</TableCell>
                    <TableCell align="right">المصاريف</TableCell>
                    <TableCell align="right">الصافي</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drawerSimpleStats && Object.keys(drawerSimpleStats.dailyData || {}).sort().map(date => (
                    <TableRow key={date}>
                      <TableCell>{date}</TableCell>
                      <TableCell align="right">{formatCurrency(drawerSimpleStats.dailyData[date].sales)}</TableCell>
                      <TableCell align="right">{formatCurrency(drawerSimpleStats.dailyData[date].maintenance)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        {formatCurrency(drawerSimpleStats.dailyData[date].debtPayments || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        -{formatCurrency(drawerSimpleStats.dailyData[date].returns || 0)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(drawerSimpleStats.dailyData[date].expenses)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(drawerSimpleStats.dailyData[date].net)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!drawerSimpleStats || Object.keys(drawerSimpleStats.dailyData || {}).length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          لا توجد بيانات خزينة في هذه الفترة
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* محتوى التبويب: محافظ انستاباي فودافون */}
      {currentTab === 'electronic-wallets' && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" />
              تقرير محافظ انستاباي فودافون
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              تفاصيل شاملة عن جميع المعاملات التي تمت بواسطة المحافظ الإلكترونية (انستاباي، فودافون كاش، اتصالات كاش)
            </Typography>



            <InstapayVodafoneWalletReport
              key={`wallet-report-${specificDate?.getTime() || new Date().getTime()}`}
              date={specificDate || new Date()}
              branchId={undefined} // يمكن إضافة دعم الفروع لاحقاً
            />
          </Paper>
        </Box>
      )}

      {/* مودال عرض تفاصيل الطلبات */}
      <Dialog
        open={showOrdersModal}
        onClose={() => setShowOrdersModal(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: '2.5vh auto'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2,
          bgcolor: 'primary.main',
          color: 'white',
          minHeight: '100px'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewIcon sx={{ color: 'white', fontSize: '2rem' }} />
              <Typography variant="h5" fontWeight="bold">
                📋 {ordersModalTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
              <Chip
                label={`${detailedOrders.length} طلب`}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  bgcolor: 'white',
                  color: 'primary.main'
                }}
              />
              <Chip
                label={`الإجمالي: ${formatCurrency(ordersTotalAmount)}`}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  bgcolor: 'success.main',
                  color: 'white'
                }}
              />
              <Chip
                label={`المدفوع: ${formatCurrency(ordersTotalPaid)}`}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  bgcolor: 'info.main',
                  color: 'white'
                }}
              />
              <Chip
                label={`المتبقي: ${formatCurrency(ordersTotalRemaining)}`}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  bgcolor: 'warning.main',
                  color: 'white'
                }}
              />
            </Box>

            {/* مؤشر حالة التحديث وزر التحديث السريع */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {autoRefresh ? '🔄 تحديث تلقائي نشط (كل 30 ثانية)' : '⏸️ تحديث يدوي'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                • آخر تحديث: {lastRefresh.toLocaleTimeString('ar-EG')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={loadDetailedOrders}
                disabled={loadingOrders}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 1,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
                startIcon={loadingOrders ? <span>⏳</span> : <span>🔄</span>}
              >
                {loadingOrders ? 'جاري التحديث...' : 'تحديث'}
              </Button>
            </Box>

            {/* رسالة توضيحية للاختصارات */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
                💡 اضغط على أي صف لفتح الطلب • استخدم زر 📋 لنسخ رقم الطلب • زر 🚀 لفتح في تبويب جديد
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setShowOrdersModal(false)}
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: 'calc(95vh - 180px)', overflow: 'auto' }}>
          {loadingOrders ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
              p: 3,
              bgcolor: 'primary.light',
              color: 'white'
            }}>
              <CircularProgress size={80} sx={{ color: 'white', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                🔄 جاري تحميل الطلبات...
              </Typography>
              <Typography variant="body1">
                يرجى الانتظار بينما نجلب البيانات
              </Typography>
            </Box>
          ) : detailedOrders.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, p: 4 }}>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                📭 لا توجد طلبات للفترة المحددة
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                لم يتم العثور على أي طلبات صيانة في هذه الفترة
              </Typography>
              <Typography variant="body1" color="info.main" sx={{ mb: 2, textAlign: 'center' }}>
                💡 تأكد من أن هناك طلبات صيانة لها مدفوعات في الفترة المحددة
              </Typography>
              <Typography variant="body1" color="warning.main" fontWeight="bold">
                📅 الفترة المحددة: {startDate?.toLocaleDateString('ar-EG')} - {endDate?.toLocaleDateString('ar-EG')}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{
              height: 'calc(95vh - 220px)',
              overflowX: 'auto',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'grey.100'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'primary.main',
                borderRadius: '4px'
              }
            }}>
              <Table stickyHeader size="small" sx={{ minWidth: '1000px' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '100px'
                    }}>
                      رقم الطلب
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '120px'
                    }}>
                      اسم العميل
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '110px'
                    }}>
                      رقم الهاتف
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '100px'
                    }}>
                      نوع الجهاز
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '80px'
                    }}>
                      الحالة
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '110px'
                    }}>
                      التكلفة الإجمالية
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '100px'
                    }}>
                      المدفوع
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '100px'
                    }}>
                      المتبقي
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '110px'
                    }}>
                      حالة الدفع
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '110px'
                    }}>
                      تاريخ التسليم
                    </TableCell>
                    <TableCell sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      padding: '8px 6px',
                      minWidth: '80px'
                    }}>
                      الإجراءات
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailedOrders.map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: 'primary.light',
                          '& .MuiTableCell-root': {
                            color: 'white'
                          }
                        },
                        '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                        minHeight: '45px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => {
                        window.open(`/admin/maintenance/order-${order.orderNumber}`, '_blank');
                      }}
                    >
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px',
                        fontWeight: 500
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: 'primary.main',
                          color: 'white',
                          px: 1,
                          py: 0.2,
                          borderRadius: 1,
                          width: 'fit-content'
                        }}>
                          <Typography variant="body2" fontWeight="600">
                            #{order.orderNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px',
                        maxWidth: '150px'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '8px',
                            bgcolor: 'success.main'
                          }} />
                          <Typography variant="body2" fontWeight="600" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {order.customerName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: 'info.light',
                          color: 'info.contrastText',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                          width: 'fit-content'
                        }}>
                          <span style={{ fontSize: '12px' }}>📞</span>
                          <Typography variant="body2" fontWeight="600">
                            {(order as any).phone || (order as any).phoneNumber || 'غير محدد'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px',
                        maxWidth: '120px'
                      }}>
                        <Typography variant="body2" sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {order.devices?.[0]?.deviceType || order.deviceType || 'غير محدد'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Tooltip
                          title={
                            order.status === MaintenanceStatus.COMPLETED ? 'تم إصلاح الجهاز وهو جاهز للتسليم' :
                            order.status === MaintenanceStatus.IN_PROGRESS ? 'جاري العمل على إصلاح الجهاز' :
                            order.status === MaintenanceStatus.PENDING ? 'الطلب في انتظار بدء الصيانة' :
                            order.status === MaintenanceStatus.CANCELLED ? 'في انتظار الصيانة - تم إرجاع الجهاز قبل البدء' :
                            order.status === MaintenanceStatus.REFUNDED ? 'في فترة ضمان الصيانة - تم إرجاع الجهاز مع استرداد المبلغ' :
                            order.status === MaintenanceStatus.RETURNED ? 'تعدى مدة الضمان - تم إرجاع الجهاز مع رفض الإصلاح' :
                            order.status === MaintenanceStatus.DELIVERED ? 'تم تسليم الجهاز للعميل' :
                            order.status
                          }
                          placement="top"
                        >
                          <Chip
                            label={order.status === MaintenanceStatus.COMPLETED ? 'مُسلم' :
                                  order.status === MaintenanceStatus.IN_PROGRESS ? 'قيد التنفيذ' :
                                  order.status === MaintenanceStatus.PENDING ? 'في الانتظار' :
                                  order.status === MaintenanceStatus.CANCELLED ? 'مرتجع' :
                                  order.status === MaintenanceStatus.REFUNDED ? 'مُسترد' :
                                  order.status}
                            color={order.status === MaintenanceStatus.COMPLETED ? 'success' :
                                  order.status === MaintenanceStatus.IN_PROGRESS ? 'warning' :
                                  order.status === MaintenanceStatus.CANCELLED ? 'error' : 'default'}
                            size="small"
                            sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Box sx={{
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          px: 1,
                          py: 0.2,
                          borderRadius: 0.5,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(order.finalCost || order.estimatedCost || order.initialCost || 0)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Box sx={{
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                          px: 1,
                          py: 0.2,
                          borderRadius: 0.5,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(order.totalPaid || order.paid || 0)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Box sx={{
                          bgcolor: order.remainingAmount && order.remainingAmount > 0 ? 'error.light' : 'grey.200',
                          color: order.remainingAmount && order.remainingAmount > 0 ? 'error.contrastText' : 'text.secondary',
                          px: 1,
                          py: 0.2,
                          borderRadius: 0.5,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(order.remainingAmount ||
                              Math.max(0, (order.finalCost || order.estimatedCost || order.initialCost || 0) - (order.totalPaid || order.paid || 0))
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Chip
                          label={(() => {
                            const totalCost = order.finalCost || order.estimatedCost || order.initialCost || 0;
                            const totalPaid = order.totalPaid || order.paid || 0;
                            const remaining = totalCost - totalPaid;

                            if (totalPaid === 0) {
                              return 'غير مدفوع';
                            } else if (remaining <= 0) {
                              return 'مدفوع بالكامل';
                            } else if (order.paymentStatus === 'partiallyPaid') {
                              return 'مدفوع جزئياً';
                            } else if (order.hasDebt) {
                              return 'دين';
                            } else {
                              return 'مدفوع جزئياً';
                            }
                          })()}
                          color={(() => {
                            const totalCost = order.finalCost || order.estimatedCost || order.initialCost || 0;
                            const totalPaid = order.totalPaid || order.paid || 0;
                            const remaining = totalCost - totalPaid;

                            if (totalPaid === 0) {
                              return 'error';
                            } else if (remaining <= 0) {
                              return 'success';
                            } else if (order.hasDebt) {
                              return 'warning';
                            } else {
                              return 'info';
                            }
                          })()}
                          size="small"
                          sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px'
                      }}>
                        <Typography variant="body2" color="text.primary">
                          {order.deliveredAt ?
                            (typeof order.deliveredAt === 'string' ?
                              new Date(order.deliveredAt).toLocaleDateString('ar-EG') :
                              order.deliveredAt instanceof Date ?
                                order.deliveredAt.toLocaleDateString('ar-EG') :
                                (order.deliveredAt as any).seconds ?
                                  new Date((order.deliveredAt as any).seconds * 1000).toLocaleDateString('ar-EG') :
                                  'تاريخ غير صحيح'
                            ) :
                            'لم يُسلم بعد'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell sx={{
                        fontSize: '0.875rem',
                        padding: '8px 6px',
                        textAlign: 'center'
                      }}
                      onClick={(e) => e.stopPropagation()} // منع فتح الطلب عند الضغط على الأزرار
                      >
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="فتح الطلب في تبويب جديد">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/admin/maintenance/order-${order.orderNumber}`, '_blank');
                              }}
                              sx={{
                                '&:hover': {
                                  bgcolor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="نسخ رقم الطلب">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(order.orderNumber.toString());
                                setSnackbar({
                                  open: true,
                                  message: `تم نسخ رقم الطلب: ${order.orderNumber}`,
                                  severity: 'success'
                                });
                              }}
                              sx={{
                                '&:hover': {
                                  bgcolor: 'secondary.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <span style={{ fontSize: '14px' }}>📋</span>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 0.5, borderColor: 'divider', flexDirection: 'column', alignItems: 'stretch' }}>
          {/* ملخص مالي مفصل */}
          <Box sx={{
            mb: 0.5,
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'grey.200'
          }}>
            {/* عنوان الملخص */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                📊 الملخص المالي للطلبات
              </Typography>
              <Chip
                label={`${detailedOrders.length} طلب`}
                color="primary"
                size="small"
              />
            </Box>

            {/* الإجماليات المالية */}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Box sx={{
                  p: 0.5,
                  bgcolor: 'primary.50',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'primary.200',
                  textAlign: 'center'
                }}>
                  <Typography variant="caption" color="primary.main" fontWeight="bold">
                    💰 إجمالي التكلفة
                  </Typography>
                  <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                    {formatCurrency(ordersTotalAmount)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{
                  p: 0.5,
                  bgcolor: 'success.50',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'success.200',
                  textAlign: 'center'
                }}>
                  <Typography variant="caption" color="success.main" fontWeight="bold">
                    💚 إجمالي المدفوع
                  </Typography>
                  <Typography variant="subtitle1" color="success.main" fontWeight="bold">
                    {formatCurrency(ordersTotalPaid)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{
                  p: 0.5,
                  bgcolor: ordersTotalRemaining > 0 ? 'error.50' : 'grey.50',
                  borderRadius: 2,
                  border: 1,
                  borderColor: ordersTotalRemaining > 0 ? 'error.200' : 'grey.200',
                  textAlign: 'center'
                }}>
                  <Typography
                    variant="caption"
                    color={ordersTotalRemaining > 0 ? "error.main" : "text.secondary"}
                    fontWeight="bold"
                  >
                    💸 إجمالي المتبقي
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color={ordersTotalRemaining > 0 ? "error.main" : "text.secondary"}
                    fontWeight="bold"
                  >
                    {formatCurrency(ordersTotalRemaining)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* نسبة التحصيل */}
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                نسبة التحصيل: {ordersTotalAmount > 0 ?
                  `${((ordersTotalPaid / ordersTotalAmount) * 100).toFixed(1)}%` :
                  '0%'
                }
              </Typography>
            </Box>
          </Box>

          {/* أزرار الإجراءات */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            pt: 1,
            borderTop: 0.5,
            borderColor: 'divider',
            minHeight: '60px'
          }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Button
                onClick={() => {
                  // نسخ ملخص مالي مفصل إلى الحافظة
                  const summary = `
الملخص المالي للطلبات - ${ordersModalTitle}
عدد الطلبات: ${detailedOrders.length}
إجمالي التكلفة: ${formatCurrency(ordersTotalAmount)}
إجمالي المدفوع: ${formatCurrency(ordersTotalPaid)}
إجمالي المتبقي: ${formatCurrency(ordersTotalRemaining)}
نسبة التحصيل: ${ordersTotalAmount > 0 ? ((ordersTotalPaid / ordersTotalAmount) * 100).toFixed(1) : '0'}%
                  `.trim();
                  navigator.clipboard.writeText(summary);
                  setSnackbar({
                    open: true,
                    message: 'تم نسخ الملخص المالي إلى الحافظة',
                    severity: 'success'
                  });
                }}
                variant="outlined"
                size="small"
                startIcon={<span style={{ fontSize: '14px' }}>📋</span>}
                sx={{ fontSize: '0.875rem' }}
              >
                نسخ الملخص المالي
              </Button>

              <Button
                onClick={() => {
                  // نسخ الإجمالي فقط
                  navigator.clipboard.writeText(formatCurrency(ordersTotalAmount));
                  setSnackbar({
                    open: true,
                    message: 'تم نسخ الإجمالي إلى الحافظة',
                    severity: 'success'
                  });
                }}
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<span style={{ fontSize: '14px' }}>💰</span>}
                sx={{ fontSize: '0.875rem' }}
              >
                نسخ الإجمالي
              </Button>
            </Box>

            <Button
              onClick={() => setShowOrdersModal(false)}
              variant="contained"
              size="small"
              color="error"
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ fontSize: '0.875rem', minWidth: '80px' }}
            >
              إغلاق
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar للإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsPage;
